import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { VsArticleTable } from "@/components/VsArticleTable";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/web-design-vs-freelancer";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/web-design-vs-freelancer")({
  head: () => ({
    meta: [
        { name: "description", content: "Honest Colorado comparison of hiring a local web design agency vs. a freelancer. Cost, ownership, support, and what fits your business stage." },
      { title: "Local Web Design Agency vs. Freelancer in Colorado" },
      { name: "description", content: ARTICLE?.desc ?? "" },
      { property: "og:title", content: "Local Web Design Agency vs. Freelancer in Colorado" },
      { property: "og:description", content: ARTICLE?.desc ?? "" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({ title: ARTICLE?.title ?? "", description: ARTICLE?.desc ?? "" }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="7 min read"
      title="Local web design agency vs. solo freelancer — what actually changes"
      intro="A local web design agency brings bench depth and an SEO foundation; a freelancer brings a lower line-item price. If your site is a brochure, the freelancer is fine. If it's your top lead source — the way most of our Colorado Springs SEO clients use theirs — the agency math wins."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Web design vs. freelancer" },
      ]}
      path={PATH}
      faqs={[
        { q: "Will I own the site if an agency builds it?", a: "If you hire the right agency — yes, 100%. You own the code, the content, the domain, and the hosting. If you ever stop working with the agency, the site comes with you. No platform lock-in, no template subscriptions, no surprise migration fees. Ask in writing before you sign." },
        { q: "What does a Colorado web design agency cost vs. a freelancer?", a: "Our websites start at $2,500 one-time, owned outright, with hosting at $50/month flat. A solid Colorado freelancer typically runs $1,500–$4,000 with hosting separate. Once you add the SEO foundation, schema, and post-launch revisions that come standard with the agency build, the numbers usually land within a few hundred dollars." },
        { q: "Can a freelancer build a site that ranks?", a: "Some can — most don't, because they specialize in design or dev but not local SEO. Ask for screenshots of Google Search Console for their past client sites before you sign." },
        { q: "What happens if my freelancer disappears mid-project?", a: "It's common enough that good agencies have rescue playbooks. The first step is recovering access to your code repo, hosting, domain, and Google Business Profile. Most rescue projects can ship inside 4 weeks." },
      ]}
    >
      <p data-speakable>
        A local web design agency ships you a site you own outright, built with a local SEO foundation and supported by a bench (designer + dev + SEO + GBP). A solo freelancer is usually cheaper but bottlenecks on bandwidth and often hands off post-launch support. For most Colorado businesses that depend on the site for leads — including <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">our local SEO clients</Link> — the local web design agency wins on consistency and SEO foundation; the freelancer wins on tight budgets and simple brochure sites.
      </p>

      <h2>Structural differences</h2>
      <VsArticleTable
        caption="What changes structurally between an agency and a freelancer"
        headerA="Local web design agency"
        headerB="Solo freelancer"
        rows={[
          { label: "You own the site and the code", a: true, b: true },
          { label: "Bench depth (design + dev + SEO + GBP)", a: "Multi-discipline team", b: "One person" },
          { label: "Vacation / sick-day risk on revisions", a: "None", b: "High" },
          { label: "Local Colorado office", a: true, b: "Often remote" },
          { label: "Built-in local SEO foundation", a: true, b: "Add-on / DIY" },
          { label: "Post-launch support", a: "Same team", b: "Often handed off" },
          { label: "Insurance + business liability", a: true, b: "Often none" },
        ]}
      />

      <h2>Outcome differences — what your site actually delivers</h2>
      <VsArticleTable
        caption="At-launch and post-launch outcome differences"
        headerA="Local agency"
        headerB="Solo freelancer"
        rows={[
          { label: "Lighthouse score at launch", a: "90+ target", b: "Varies" },
          { label: "Local schema markup", a: "Built in", b: "Often skipped" },
          { label: "City-specific service pages", a: true, b: "DIY" },
          { label: "Time to launch", a: "4–8 weeks", b: "6–16 weeks (queue)" },
          { label: "Revisions after launch", a: "Owner-handled, no surprise bill", b: "Usually hourly" },
        ]}
      />

      <h2>When a freelancer is the right call</h2>
      <ul>
        <li>You need a single-page brochure site and don't expect leads from organic search.</li>
        <li>You're tech-savvy enough to add schema, optimize page speed, and write SEO copy yourself.</li>
        <li>Your budget is under $1,500 total and you're testing whether a website even moves the needle.</li>
      </ul>

      <h2>When a local web design agency is the better hire</h2>
      <p>
        If your site needs to rank in Google and Maps from day one, a local web design agency builds the SEO foundation, schema, and city-specific pages into the launch — not as a $3,000 retrofit six months later. That's why our builds ship paired with <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">Colorado Springs SEO services</Link> instead of as standalone brochures.
      </p>

      <p>
        Next read: <Link to="/learn/custom-web-design-vs-diy-platforms" className="font-bold text-brand-gold-deep underline">custom web design vs. national DIY platforms</Link>, or see our published <Link to="/pricing" className="font-bold text-brand-gold-deep underline">packages</Link>.
      </p>

      <p className="text-sm text-foreground/60 italic">
        This guide compares category labels — not specific competitors. See our <Link to="/editorial-policy" className="underline">editorial policy</Link>.
      </p>
    </ArticleLayout>
  );
}
